import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Download } from 'lucide-react';
import { fetchEventDownloadOptions, formatMoney, friendlyError } from '@/lib/api';
import { toast } from 'sonner';
import { DownloadActionButton, DownloadAssetMeta } from '@/components/downloads/DownloadActionButton';

/**
 * Fase 06D · Bloque de descarga para un Partido.
 *
 * Consume `GET /api/events/:id/download-options` y muestra el botón correcto
 * (Descargar / Comprar / Preparando / No disponible / Login). Si no existe
 * un download_asset ni un download_product, no renderiza nada.
 */
export function EventDownloadBlock({ event }) {
  const [loading, setLoading] = useState(true);
  const [options, setOptions] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!event?.id) { setLoading(false); return; }
      try {
        const data = await fetchEventDownloadOptions(event.id);
        if (alive) setOptions(data);
      } catch (err) {
        if (alive) toast.error(friendlyError(err));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [event?.id]);

  if (loading) return null;
  // No renderizar si no hay asset y no hay producto (no aporta info)
  if (!options?.asset && !options?.product) return null;

  const asset = options.asset;
  const product = options.product;
  const price = options.price_cents;
  const currency = options.currency || 'EUR';

  return (
    <Card
      data-testid="event-download-block"
      className="p-5 sm:p-6 rounded-2xl bg-white border border-[var(--ns-border)] shadow-[var(--ns-shadow-soft)]"
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="h-10 w-10 rounded-xl bg-[var(--ns-gold)]/15 text-[var(--ns-gold-700)] flex items-center justify-center shrink-0">
          <Download className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs uppercase tracking-widest text-[var(--ns-text-muted)] font-semibold">Descarga del partido</p>
          <p className="text-sm font-semibold text-[var(--ns-text)] mt-0.5 truncate">
            {product?.title || asset?.title || 'Descarga oficial'}
          </p>
          <DownloadAssetMeta asset={asset} className="mt-0.5" />
        </div>
        {product ? (
          <Badge className="bg-[var(--ns-gold)]/20 text-[var(--ns-gold-700)] text-[10px] uppercase whitespace-nowrap">
            {price === 0 ? 'Gratis' : formatMoney(price, currency)}
          </Badge>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <DownloadActionButton options={options} testIdBase="event-download" />
      </div>
    </Card>
  );
}
